"use client"

import * as React from "react"
import { Ban } from "lucide-react"
import { Alert, AlertTitle } from "@/components/ui/alert"
import { AiStateAlert } from "@/components/ai/ai-state-alert"
import { AiDegradedNotice } from "@/components/ai/ai-degraded-notice"
import { type AiError } from "@/lib/ai/errors"
import { useTranslation } from "@/lib/i18n"
import type { AiAllowance } from "@/lib/ai/credit-state"
import { cn } from "@/lib/utils"
import { usePathname } from 'next/navigation'
import { VishingChatNotice } from './vishing-chat-notice'

/**
 * The AI standing banners for a chat surface — one composition, four hosts.
 *
 * There are FOUR chat surfaces (`chat-page.tsx` plus the drawer, PiP and
 * maximized variants of `chat-companion.tsx`) and every one of them shipped the
 * same hole: `disabled={ctx.apiKeyMissing}` on the composer and NO banner
 * anywhere. The box went grey and nothing said why. Four copies of the fix
 * would have been four chances to fix three.
 *
 * This is a composition of the shared toolkit, not a second toolkit: the
 * sentences come from `lib/ai/quota-copy.ts` and `lib/ai/errors.ts`, the credit
 * ladder from `<AiStateAlert>`, the downgrade from `<AiDegradedNotice>`.
 *
 * ── The provider-missing banner ─────────────────────────────────────────────
 * `chat-page.tsx` used to render, to the CUSTOMER:
 *
 *     API key missing. Add GEMINI_API_KEY or ANTHROPIC_API_KEY to your
 *     .env.local file to use AI Chat.
 *
 * That is a developer instruction on a tenant screen; it names two providers;
 * and no admin of a hosted product has a `.env.local` to edit. The replacement
 * is the shared `no_provider_key` sentence, which names the SETTINGS page they
 * can actually reach.
 *
 * ── What renders when ───────────────────────────────────────────────────────
 *   healthy, nothing wrong  → NOTHING. A green "all good" banner above every
 *                             chat is noise that trains people to ignore amber.
 *   warning / exhausted     → `<AiStateAlert>` with the server's remedies.
 *   degraded                → `<AiDegradedNotice>`, driven off `modelTier` and
 *                             NOT off the credit state, so a run that is both
 *                             degraded and low still announces the downgrade.
 *   unknown                 → silence. A probe that could not answer must not
 *                             interrupt a chat, and the server enforces the
 *                             ceiling regardless of what this believes.
 */
export interface ChatAiNoticesProps {
  allowance: AiAllowance
  /** The last typed chat failure. Its raw detail rides under a disclosure. */
  error?: AiError | null
  /** True when the backend reports no AI provider is configured at all. */
  apiKeyMissing?: boolean
  className?: string
}

export function ChatAiNotices({
  allowance,
  error,
  apiKeyMissing,
  className,
}: ChatAiNoticesProps) {
  const pathname = usePathname()
  const { t } = useTranslation()
  const voiceResidencyError = error?.errorCode === 'AI_VISHING_VOICE_RESIDENCY'
  return (
    // `empty:hidden` — when every child returns null the wrapper contributes no
    // spacing, so the healthy case is byte-for-byte the layout it always was.
    <div className={cn("flex flex-col gap-2 empty:hidden", className)}>
      {(pathname === '/vishing' || pathname?.startsWith('/vishing/')) && <VishingChatNotice />}
      {voiceResidencyError && <Alert data-testid="vishing-chat-residency-error"><AlertTitle>{t('vishing.ai.residencyUnavailable')}</AlertTitle></Alert>}
      {apiKeyMissing === true && !voiceResidencyError && (
        <Alert variant="destructive" data-testid="chat-ai-unavailable">
          <Ban aria-hidden="true" />
          {/* The whole sentence — what is wrong AND where to fix it — is the
              one in the shared table. Adding a second line here is how the
              wording starts drifting per surface again. */}
          <AlertTitle>{t('aiErrors.no_provider_key')}</AlertTitle>
        </Alert>
      )}
      <AiStateAlert allowance={allowance} error={voiceResidencyError ? undefined : error} />
      <AiDegradedNotice modelTier={allowance.modelTier} />
    </div>
  )
}
